import { map, share } from 'rxjs/operators';
import { Partner } from './partner';
import { Injectable } from '@angular/core';
import { ApiService } from './api';
import { Observable, of } from 'rxjs';
import { PartnerType } from '../admin/admin-partners/partner-type.enum';
import { MediaFileService } from './media-file.service';

@Injectable({ providedIn: 'root' })
export class PartnersService {
  path: string = '/api';
  partners: Partner[] | null = null;

  constructor(
    private apiService: ApiService,
    private mediaFileService: MediaFileService,
  ) { }

  private observable: Observable<Partner[]> | null = null;

  getPartners(type: PartnerType): Observable<Partner[]> {
    return this.apiService.get<Partner[]>(`${this.path}/v2/${type}s`);
  }

  getPartnersV2Observable(type: PartnerType): Observable<Partner[]> {
    return this.apiService.get<Partner[]>(`${this.path}/v2/${type}s`).pipe(map((response: Partner[]) =>
      response.map((partner) => ({
        ...partner,
        type: partner.type || type,
        // List rows lack detail fields (brands, categories, etc.). Mark them so
        // resolvers force a detail fetch before the partner is treated as loaded.
        partialSeed: true,
      }))));
  }

  getAllPartners(): Observable<Partner[]> {
    if (this.partners) {
      return of(this.partners);
    } else if (this.observable) {
      return this.observable;
    } else {
      this.observable = this.apiService.get<Partner[]>(`${this.path}/partners`).pipe(
        map((response: Partner[]) => {
          this.observable = null;
          this.partners = response;
          return this.partners;
        }),
        share(),
      );
      return this.observable;
    }
  }

  getPartner(type: PartnerType, id: number): Observable<Partner> {

    if (type === PartnerType.AIRLINE) {
      return this.apiService.get<Partner>(`${this.path}/${type}s/${id}`);
    }
    return this.apiService.get<Partner>(`${this.path}/v2/${type}s/${id}`);
  }

  private getPayloadAsFormData(type: PartnerType, partner: Partner, logoFile?: File): FormData {
    const payload = new FormData();

    const containerKey = type == PartnerType.AIRLINE ? 'data' : type; 

    for (let key in partner) {
      if (['terminals', 'subfleets', 'subscriptions', 'categories', 'brands'].includes(key)) {
        continue;
      }

      if(type == PartnerType.AIRLINE && key.includes('logo')) {
        continue;
      }

      const partnerKey = key as keyof Partner;
      if (key === 'id' && !partner[partnerKey]) {
        continue;
      }

      // Partner fields aren't all string|Blob (FormData.append's only accepted value types) — some
      // are numbers/booleans/arrays/objects, relying on the browser's implicit toString() coercion.
      // TODO(strict-typing): give Partner (or a dedicated form-payload type) per-field serialization
      // instead of this blanket cast.
      payload.append(`${containerKey}[${key}]`, partner[partnerKey] as any);
    }

    if (type !== PartnerType.AIRLINE) {
      if (logoFile) {
        payload.append(`${containerKey}[logo]`, logoFile, this.mediaFileService.sanitizeFileName(logoFile.name));
      }
    }

    return payload;
  }

  updatePartner(type: PartnerType, id: number, partner: Partner, logoFile?: File): Promise<Partner> {

    const url = type === PartnerType.AIRLINE
      ? `${this.path}/${type}s/${id}`
      : `${this.path}/v2/${type}s/${id}`;

    const payload = this.getPayloadAsFormData(type, partner, logoFile);

    return this.apiService.putForm(url, payload).toPromise().then(() => partner);
  }

  addPartner(type: PartnerType, partner: Partner, logoFile?: File): Promise<Partner> {
    const url = type === PartnerType.AIRLINE
      ? `${this.path}/${type}s`
      : `${this.path}/v2/${type}s`;
    const payload = this.getPayloadAsFormData(type, partner, logoFile);

    // toPromise() types the resolved value as Partner | undefined because RxJS can't guarantee an
    // observable emits before completing; ApiService's HTTP observables always emit exactly once
    // on success (or reject via HTTP error), so the assertion holds here.
    return this.apiService.postForm<Partner>(url, payload).toPromise().then(result => result!);
  }

}
